/**
 * The wire contract between the browser and the sync Worker.
 *
 * The server never sees plaintext: it stores an opaque blob under an id that
 * is a one-way hash of the sync code, so it can neither read the data nor
 * derive the key from the id it holds. That means the server also cannot
 * merge — every merge happens in the browser, and `rev` is the only thing the
 * Worker understands, purely to reject a write built on a stale read.
 *
 * Imported by the Worker as a TYPE ONLY, so nothing from the app bundle ends
 * up in the server build.
 */

/** Bumped only if the sealed format changes; unrelated to the app's schemaVersion. */
export const ENVELOPE_VERSION = 1;

export interface Envelope {
  v: typeof ENVELOPE_VERSION;
  /**
   * Monotonic, server-enforced. A PUT must carry exactly `stored.rev + 1`
   * (or 1 when nothing is stored), which is what turns a blind overwrite into
   * a detectable conflict.
   */
  rev: number;
  /** ISO 8601, for the "last synced" line. Advisory — the client's clock. */
  updatedAt: string;
  /** base64 AES-GCM nonce, 12 bytes. */
  iv: string;
  /** base64 AES-GCM ciphertext of the JSON payload. */
  data: string;
}

/** Structural check only — the ciphertext can only be validated by decrypting it. */
export function isEnvelope(value: unknown): value is Envelope {
  if (typeof value !== 'object' || value === null) return false;
  const e = value as Partial<Envelope>;
  return (
    e.v === ENVELOPE_VERSION &&
    typeof e.rev === 'number' &&
    Number.isInteger(e.rev) &&
    e.rev > 0 &&
    typeof e.updatedAt === 'string' &&
    typeof e.iv === 'string' &&
    typeof e.data === 'string'
  );
}

/** Body of a 409, so the client can merge against what is actually stored. */
export interface ConflictBody {
  error: 'conflict';
  current: Envelope | null;
}

export function syncUrl(docId: string): string {
  return `/api/sync/${docId}`;
}
